import { DocumentNode } from 'graphql';
import { isEqual, uniq } from 'lodash';
import { graphql } from 'react-apollo';
import { compose } from 'recompose';
import { AthleteOptionsMutation } from './queries';
import {
    AthleteOptions,
    AthleteProps,
    CustomStreamType,
    MoveDirection,
    SaveAthleteOptionsProps,
    StravaResolution,
    StravaStreamType
} from './types';
import { DEFAULT_RESOLUTION, fetchScalarStreams } from './withActivityStreams';
import withAthlete from './withAthlete';

const getAthleteOptionsPayload = (
    id: string,
    athleteOptions: AthleteOptions
): {} => ({
    __typename: 'Mutation',
    athleteOptions: {
        __typename: 'AthleteOptionsPayload',
        athlete: {
            __typename: 'Athlete',
            id: id + '',
            ...athleteOptions
        }
    }
});

/**
 * Returns a HOC which provides methods to mutate the athlete options
 * @param query Graphql query to save athlete options
 * @param getOptimisticPayload Generates an optimistic payload for the provided graphql query
 * @param fetchStreams Provide a function which allows streams to be prefetched, to avoid UI flashing
 */
export const withSaveAthleteOptionsCore = (
    query: DocumentNode,
    getOptimisticPayload: (id: string, athleteOptions: AthleteOptions) => {},
    fetchStreams?: (
        props: {
            id: string;
            resolution: StravaResolution;
            streamTypes: (StravaStreamType | CustomStreamType)[];
        }
    ) => Promise<any>
) =>
    graphql<
        AthleteProps,
        any,
        {
            input: AthleteOptions;
        },
        AthleteProps & SaveAthleteOptionsProps
    >(query, {
        props: ({
            ownProps,
            mutate
        }): AthleteProps & SaveAthleteOptionsProps => {
            if (!mutate) {
                throw new Error('No mutation function provided');
            }

            const athlete =
                ownProps.loggedInUser &&
                ownProps.loggedInUser.user &&
                ownProps.loggedInUser.user.athlete;

            // Don't add the methods if we don't have an athlete
            if (!athlete || ownProps.loading || ownProps.error) {
                return { ...ownProps };
            }

            // Make sure the distance and time streams aren't selected for the charts
            const prevActivityStreamTypes = (
                athlete.activityStreamTypes || []
            ).filter(
                t =>
                    t !== StravaStreamType.distance &&
                    t !== StravaStreamType.time
            );

            const prevAthleteOptions: AthleteOptions = {
                activityStreamTypes: prevActivityStreamTypes,
                autoplayAnimation: athlete.autoplayAnimation,
                resolution: athlete.resolution,
                selectedActivityStreamType: athlete.selectedActivityStreamType,
                unitsPreference: athlete.unitsPreference
            };

            const saveAthleteOptions = (
                athleteOptions: AthleteOptions
            ): Promise<any> =>
                !isEqual(athleteOptions, prevAthleteOptions)
                    ? mutate({
                          optimisticResponse: getOptimisticPayload(
                              athlete.id || '',
                              athleteOptions
                          ),
                          variables: { input: athleteOptions }
                      })
                    : Promise.resolve();

            return {
                ...ownProps,
                addActivityStream: async (
                    streamType: StravaStreamType | CustomStreamType,
                    fetchStreamForActivityId?: string
                ) => {
                    if (fetchStreamForActivityId && fetchStreams) {
                        await fetchStreams({
                            id: fetchStreamForActivityId,
                            resolution:
                                prevAthleteOptions.resolution ||
                                DEFAULT_RESOLUTION,
                            streamTypes: [streamType]
                        });
                    }

                    let activityStreamTypes =
                        (prevAthleteOptions.activityStreamTypes &&
                            prevAthleteOptions.activityStreamTypes.slice()) ||
                        [];

                    // Insert the new item at the top of the list
                    const index = activityStreamTypes.indexOf(streamType);
                    if (index === -1) {
                        activityStreamTypes.splice(0, 0, streamType);
                    }
                    // Make sure the new type isn't already in the list
                    activityStreamTypes = uniq(activityStreamTypes);

                    return saveAthleteOptions({
                        ...prevAthleteOptions,
                        activityStreamTypes,
                        selectedActivityStreamType: streamType
                    });
                },
                moveActivityStream: (streamType, direction) => {
                    const activityStreamTypes = prevActivityStreamTypes.slice();
                    const index = activityStreamTypes.indexOf(streamType);

                    if (index === -1) {
                        throw new Error('Stream type not found');
                    }

                    if (
                        direction === MoveDirection.down &&
                        index < activityStreamTypes.length - 1
                    ) {
                        activityStreamTypes.splice(index, 1);
                        activityStreamTypes.splice(index + 1, 0, streamType);
                    } else if (direction === MoveDirection.up && index > 0) {
                        activityStreamTypes.splice(index, 1);
                        activityStreamTypes.splice(index - 1, 0, streamType);
                    } else {
                        // Do nothing if we can't move the item
                        return;
                    }

                    return saveAthleteOptions({
                        ...prevAthleteOptions,
                        activityStreamTypes
                    });
                },
                removeActivityStream: streamType => {
                    const types =
                        (prevAthleteOptions.activityStreamTypes &&
                            athlete.activityStreamTypes.slice()) ||
                        [];

                    // Remove the stream
                    const index = types.indexOf(streamType);
                    if (index >= 0) {
                        types.splice(types.indexOf(streamType), 1);
                    }

                    // If the removed stream was selected,
                    // select the stream after it, or the last stream in the list
                    let selected = athlete.selectedActivityStreamType;
                    if (!selected || streamType === selected) {
                        selected = types.length
                            ? types[Math.min(types.length - 1, index)]
                            : null;
                    }

                    return saveAthleteOptions({
                        ...prevAthleteOptions,
                        activityStreamTypes: types,
                        selectedActivityStreamType: selected
                    });
                },
                selectActivityStream: streamType =>
                    saveAthleteOptions({
                        ...prevAthleteOptions,
                        selectedActivityStreamType: streamType || null
                    }),
                toggleAutoplayAnimation: autoplay =>
                    saveAthleteOptions({
                        ...prevAthleteOptions,
                        autoplayAnimation:
                            typeof autoplay !== 'undefined'
                                ? !!autoplay
                                : !prevAthleteOptions.autoplayAnimation
                    }),
                updateUnitsPreference: unitsPreference =>
                    saveAthleteOptions({
                        ...prevAthleteOptions,
                        unitsPreference
                    })
            };
        }
    });

export default compose<AthleteProps & SaveAthleteOptionsProps, any>(
    withAthlete,
    withSaveAthleteOptionsCore(
        AthleteOptionsMutation,
        getAthleteOptionsPayload,
        fetchScalarStreams
    )
);



// WEBPACK FOOTER //
// ./src/client/components/Strava/withSaveAthleteOptions.tsx